import IconButton from '@/global/components/Button/IconButton'
import { cn } from '@/lib/utils'
import {
    formatPorcentagemRegra,
    minutesToHours as hr,
    hoursToMinutes as min,
} from '@acme/common'
import { InfoIcon } from 'lucide-react'
import React from 'react'
import VerticalDivider from './Divider/VerticalDivider'
import Menu from './Menu'

type TKey = 'Parciais' | 'Integrais' | 'Justificadas' | 'Injustificadas'

type TipoFaltas = {
    [key in TKey]: string | undefined
}

interface IPropsSaldo {
    title: React.ReactNode
    extras?: string
    faltas?: string
    porcentagens?: { [key: string]: string }
    porcentagensNoturnas?: { [key: string]: string }
    tipoFaltas?: TipoFaltas
    className?: string
    underline?: boolean
    /**
     * Box de saldo (anterior/atual): valores não-zerados ganham cor e sinal —
     * extras em verde com '+', faltas em vermelho com '-'. Valores zerados
     * sempre aparecem em `text-muted-foreground`.
     */
    saldo?: boolean
}

const BOTAO_INFO_CLASS = 'size-fit p-0 -translate-y-px'
const SPAN_CLASS =
    'text-nowrap text-sm font-semibold flex items-center justify-evenly gap-1'

type DetalheSaldo = { chave: string; rotulo: string; valor: string }

const isZeroHora = (value?: string) => !value || min(value) === 0

export default function SaldoBox({
    extras = '00:00',
    faltas = '00:00',
    porcentagens = {},
    porcentagensNoturnas = {},
    tipoFaltas = {
        Parciais: '00:00',
        Integrais: '00:00',
        Justificadas: '00:00',
        Injustificadas: '00:00',
    },
    title,
    className,
    underline,
    saldo,
}: IPropsSaldo) {
    const rotulo = (tipo: 'Extras' | 'Faltas') => (saldo ? '' : `${tipo}: `)
    const mostrarExtras = !saldo || !isZeroHora(extras)
    const mostrarFaltas = !saldo || !isZeroHora(faltas)

    const exibirTiposFaltas =
        faltas !== '00:00' &&
        Object.values(tipoFaltas).some(v => !!v && v !== '00:00')

    const detalhesExtras: DetalheSaldo[] = [
        ...Object.entries(porcentagens).map(([key, valor]) => ({
            chave: key,
            rotulo: `Horas extras ${formatPorcentagemRegra(key)}`,
            valor: hr(min(valor)),
        })),
        ...Object.entries(porcentagensNoturnas).map(([key, valor]) => ({
            chave: `noturno_${key}`,
            rotulo: `Horas extras noturno ${formatPorcentagemRegra(key)}`,
            valor: hr(min(valor)),
        })),
    ]
    const detalhesFaltas: DetalheSaldo[] = exibirTiposFaltas
        ? Object.entries(tipoFaltas).map(([key, valor]) => ({
              chave: key,
              rotulo: `Faltas ${key}`,
              valor: valor || '00:00',
          }))
        : []
    const hasAlgumDetalhe =
        (mostrarExtras && detalhesExtras.length > 0) ||
        (mostrarFaltas && detalhesFaltas.length > 0)

    const valorClasse = (value: string, tipo: 'extras' | 'faltas') => {
        if (isZeroHora(value)) return 'text-muted-foreground'
        if (!saldo) return undefined
        return tipo === 'extras' ? 'text-success' : 'text-error'
    }

    const valorPrefixo = (value: string, tipo: 'extras' | 'faltas') => {
        if (isZeroHora(value) || !saldo) return ''
        return tipo === 'extras' ? '+' : '-'
    }

    return (
        <div
            className={cn(
                'flex gap-1 items-center border rounded-sm py-0.5 px-2',
                className,
            )}
        >
            <span
                className={cn(
                    'text-sm font-normal text-nowrap text-right',
                    underline ? 'underline' : 'decoration-inherit',
                )}
            >
                {title}
            </span>
            <VerticalDivider />
            <div>
                {saldo && !mostrarExtras && !mostrarFaltas && (
                    <div className="text-nowrap text-sm font-semibold text-muted-foreground">
                        00:00
                    </div>
                )}
                {mostrarExtras && (
                    <div className={SPAN_CLASS}>
                        <span className={valorClasse(extras, 'extras')}>
                            {`${rotulo('Extras')}${valorPrefixo(extras, 'extras')}${extras}`}
                        </span>
                        <MenuDetalhes
                            itens={detalhesExtras}
                            hasPlaceholder={hasAlgumDetalhe}
                        />
                    </div>
                )}
                {mostrarFaltas && (
                    <div className={SPAN_CLASS}>
                        <span className={valorClasse(faltas, 'faltas')}>
                            {`${rotulo('Faltas')}${valorPrefixo(faltas, 'faltas')}${faltas}`}
                        </span>
                        <MenuDetalhes
                            itens={detalhesFaltas}
                            hasPlaceholder={hasAlgumDetalhe}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

interface PropsMenuDetalhes {
    itens: DetalheSaldo[]
    /** Reserva o espaço do botão quando vazio, mantendo os valores alinhados às linhas com menu. */
    hasPlaceholder: boolean
}

function MenuDetalhes({ itens, hasPlaceholder }: PropsMenuDetalhes) {
    if (!itens.length) {
        return hasPlaceholder ? <span className="size-4 shrink-0" /> : null
    }

    return (
        <Menu.Root position="top-center" arrow>
            <Menu.Trigger>
                <IconButton size="small" className={BOTAO_INFO_CLASS}>
                    <InfoIcon className="size-4" />
                </IconButton>
            </Menu.Trigger>
            <Menu.List>
                {itens.map(({ chave, rotulo, valor }) => (
                    <Menu.Item
                        key={chave}
                        className="flex justify-between gap-4 cursor-default"
                    >
                        <span className="text-nowrap">{rotulo}:</span>
                        <span className="text-nowrap">{valor}</span>
                    </Menu.Item>
                ))}
            </Menu.List>
        </Menu.Root>
    )
}
